import { ApolloClient } from 'apollo-client';
import { from, split, type ApolloLink } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import { HttpLink } from 'apollo-link-http';
import { BatchHttpLink } from 'apollo-link-batch-http';
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';

import { expValEquals } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';

import fragmentMatcherAGGTypes from './types/fragmentMatcherAGGTypes.json';
import { createOnErrorLink } from './links/OnErrorLink.ts';
import { SSRTestLink } from './links/SSRTestLink.ts';
import { SSRLink } from './links/SSRLink.ts';
import { createWrappedFetch } from './createWrappedFetch.ts';
import { getAGGWebSocketLinkInstance } from './links/AGGWebSocketLink/AGGWebSocketLink.ts';
import { AttachExtensionsLink } from './links/AGGWebSocketLink/AttachExtensionsLink.ts';
import { SSRDataLink } from './links/SSRServerLink.ts';
import { SSRClientLink } from './links/SSRClientLink.ts';
import { BroadcastConfigurableInMemoryCache } from './broadcastConfigurableInMemoryCache.ts';
import { requestIdLink } from './links/RequestIdLink.ts';
import { isFastPreloadQuery } from './preloadQueryBatchConfig.ts';
import { DEFAULT_MAX_BATCH_SIZE, DEFAULT_BATCH_INTERVAL } from './batchingConfig.ts';

let apolloClient: ApolloClient<Object> | null = null;
const wrappedFetch = createWrappedFetch(true);
export const SSR_AGG_NAMESPACE = 'agg';

const fragmentMatcher = new IntrospectionFragmentMatcher({
	introspectionQueryResultData: {
		__schema: {
			types: fragmentMatcherAGGTypes,
		},
	},
});

export const apolloAGGCache = new BroadcastConfigurableInMemoryCache({ fragmentMatcher });

// export for testing
export const clearAGGClient = (): void => {
	apolloClient = null;
};

export const getAGGClient = (): ApolloClient<object> => {
	// Use mock gateway Apollo client in tests
	if ((window as any).MOCK_GATEWAY_APOLLO_CLIENT) {
		return (window as any).MOCK_GATEWAY_APOLLO_CLIENT;
	}

	if (!apolloClient) {
		apolloClient = createApolloClient();
		(window as any).GLOBAL_APOLLO_AGG_CLIENT = apolloClient;
	}
	return apolloClient;
};

const createLinks = () => {
	const subscriptionLinks: ApolloLink[] = [];

	const httpLinkOptions = {
		credentials: 'include' as const,
		uri: '/gateway/api/graphql',
		fetch: wrappedFetch,
	};

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const ssrLinks: ApolloLink[] = [createOnErrorLink()];
		ssrLinks.push(SSRDataLink(SSR_AGG_NAMESPACE), SSRLink());
		if (process.env.CLOUD_ENV === 'staging' || process.env.CLOUD_ENV === 'branch') {
			ssrLinks.push(SSRTestLink());
		}

		const httpLink = split(
			(operation) => {
				// Always respect explicit single-query context
				if (operation.getContext().single) {
					return true;
				}
				// When the feedback loop experiment is enabled, send slow/unknown queries
				// individually to avoid them delaying fast queries in the same batch.
				// Note: expValEquals is evaluated at request time (not link creation time)
				// because Statsig is not yet initialized when links are created.
				if (expValEquals('new_query_batching_feedback_loop', 'isEnabled', true)) {
					return !isFastPreloadQuery(operation.operationName);
				}
				return false;
			},
			new HttpLink(httpLinkOptions),
			new BatchHttpLink({
				...httpLinkOptions,
				batchMax: DEFAULT_MAX_BATCH_SIZE,
				batchInterval: DEFAULT_BATCH_INTERVAL,
			}),
		);

		return from(ssrLinks).concat(httpLink);
	} else {
		const processingLinks: ApolloLink[] = [createOnErrorLink()];

		if (expValEquals('cfe_use_persisted_queries_apollo', 'isEnabled', true)) {
			processingLinks.push(requestIdLink);
		}
		processingLinks.push(SSRClientLink(SSR_AGG_NAMESPACE, apolloAGGCache));

		subscriptionLinks.push(AttachExtensionsLink);
		subscriptionLinks.push(getAGGWebSocketLinkInstance());

		const httpLink = split(
			({ getContext }) => {
				return (
					getContext().single ||
					// In integration tests we deliberately block some queries in order to test the loading state
					// Randomly batching other queries with them will make UI fail to load
					// @ts-ignore
					window.Cypress ||
					window.__CRITERION__ ||
					// @ts-ignore Property 'isPlaywright' does not exist on type 'Window & typeof globalThis'
					Boolean(window.isPlaywright) ||
					// we don't want query batching for contract tests
					// @ts-expect-error Property isContract doesn't exist on window
					Boolean(window.isContract) ||
					!StatsigConfigurations.isKillSwitchOn('confluence_spa_query_batching')
				);
			},
			new HttpLink(httpLinkOptions),
			new BatchHttpLink(httpLinkOptions),
		);

		return split(
			// Condition for handling subscriptions
			// we don't want to use subscriptions in SSR mode
			({ query }) => {
				const definition = getMainDefinition(query);
				return (
					definition?.kind === 'OperationDefinition' && definition?.operation === 'subscription'
				);
			},
			// WebSocket link for subscriptions
			from(subscriptionLinks),
			// HTTP link for non-subscription operations
			from(processingLinks).concat(httpLink),
		);
	}
};

const createApolloClient = (): ApolloClient<object> => {
	if (typeof window['__APOLLO_AGG_STATE__'] === 'object') {
		apolloAGGCache.restore(window['__APOLLO_AGG_STATE__']);
	}
	const client = new ApolloClient({
		// Prevent cache and network query to refresh when they are already preloaded on server-side.
		// Refreshing data will cause component to fallback to loading state.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		ssrMode: Boolean(process.env.REACT_SSR),
		connectToDevTools: process.env.NODE_ENV !== 'production',
		link: from([createLinks()]),
		cache: apolloAGGCache,
	});

	// This is not actual API from Apollo. This field is read in next/packages/ssr-app/src/module-replacements/apollo-client.js
	(client as any).__isAGGClient = true;
	return client;
};
